import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const AuditTrail = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  const auditRecords = [
    {
      id: 1,
      timestamp: '2025-09-21T09:45:23Z',
      action: 'Trade Execution',
      category: 'EUA',
      user: 'John Trader',
      details: 'Executed EUA trade: 1,000 units at €85.50',
      transactionId: 'EUA-2025-0921-001',
      status: 'completed',
      ipAddress: '192.168.1.100',
      riskScore: 'low'
    },
    {
      id: 2,
      timestamp: '2025-09-21T09:30:15Z',
      action: 'Position Limit Check',
      category: 'Oil',
      user: 'System',
      details: 'Automated position limit validation for WTI futures',
      transactionId: 'OIL-2025-0921-045',
      status: 'passed',
      ipAddress: 'System',
      riskScore: 'low'
    },
    {
      id: 3,
      timestamp: '2025-09-21T09:15:42Z',
      action: 'Compliance Report Generated',
      category: 'General',
      user: 'Sarah Chen',
      details: 'Generated daily compliance summary report',
      transactionId: 'RPT-2025-0921-003',
      status: 'completed',
      ipAddress: '192.168.1.105',
      riskScore: 'low'
    },
    {
      id: 4,
      timestamp: '2025-09-21T08:55:18Z',
      action: 'Risk Alert Triggered',
      category: 'Oil',
      user: 'System',
      details: 'Position concentration alert: Oil futures exposure exceeds 75% threshold',
      transactionId: 'ALERT-2025-0921-012',
      status: 'acknowledged',
      ipAddress: 'System',
      riskScore: 'high'
    },
    {
      id: 5,
      timestamp: '2025-09-21T08:30:07Z',
      action: 'Document Upload',
      category: 'EUA',
      user: 'Michael Rodriguez',
      details: 'Uploaded EUA registry verification documents',
      transactionId: 'DOC-2025-0921-008',
      status: 'pending_review',
      ipAddress: '192.168.1.102',
      riskScore: 'medium'
    },
    {
      id: 6,
      timestamp: '2025-09-21T08:00:33Z',
      action: 'User Login',
      category: 'Security',
      user: 'David Kim',
      details: 'Successful login with 2FA verification',
      transactionId: 'AUTH-2025-0921-025',
      status: 'completed',
      ipAddress: '192.168.1.108',
      riskScore: 'low'
    },
    {
      id: 7,
      timestamp: '2025-09-20T17:45:12Z',
      action: 'Regulatory Filing',
      category: 'Oil',
      user: 'Lisa Wang',
      details: 'Submitted CFTC large trader position report',
      transactionId: 'CFTC-2025-0920-001',
      status: 'submitted',
      ipAddress: '192.168.1.110',
      riskScore: 'low'
    },
    {
      id: 8,
      timestamp: '2025-09-20T16:20:45Z',
      action: 'Trade Cancellation',
      category: 'EUA',
      user: 'Emma Davis',
      details: 'Cancelled EUA trade due to compliance violation',
      transactionId: 'EUA-2025-0920-089',
      status: 'cancelled',
      ipAddress: '192.168.1.115',
      riskScore: 'high'
    }
  ];

  const filterOptions = [
    { value: 'all', label: 'All Activities' },
    { value: 'EUA', label: 'EUA Trading' },
    { value: 'Oil', label: 'Oil Futures' },
    { value: 'General', label: 'General' },
    { value: 'Security', label: 'Security' }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-success bg-success/10 border-success/20';
      case 'submitted': return 'text-accent bg-accent/10 border-accent/20';
      case 'passed': return 'text-success bg-success/10 border-success/20';
      case 'acknowledged': return 'text-warning bg-warning/10 border-warning/20';
      case 'pending_review': return 'text-warning bg-warning/10 border-warning/20';
      case 'cancelled': return 'text-error bg-error/10 border-error/20';
      default: return 'text-muted-foreground bg-muted border-border';
    }
  };

  const getRiskScoreColor = (score) => {
    switch (score) {
      case 'low': return 'text-success';
      case 'medium': return 'text-warning';
      case 'high': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'Trade Execution': return 'TrendingUp';
      case 'Position Limit Check': return 'Shield';
      case 'Compliance Report Generated': return 'FileText';
      case 'Risk Alert Triggered': return 'AlertTriangle';
      case 'Document Upload': return 'Upload';
      case 'User Login': return 'LogIn';
      case 'Regulatory Filing': return 'Send';
      case 'Trade Cancellation': return 'X';
      default: return 'Activity';
    }
  };

  const filteredRecords = auditRecords?.filter(record => {
    const matchesSearch = searchTerm === '' || 
      record?.details?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      record?.user?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      record?.transactionId?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    
    const matchesFilter = selectedFilter === 'all' || record?.category === selectedFilter;
    
    return matchesSearch && matchesFilter;
  });

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return {
      date: date?.toLocaleDateString(),
      time: date?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-subtle">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-accent/10 rounded-lg flex items-center justify-center">
              <Icon name="Activity" size={18} className="text-accent" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Audit Trail</h2>
              <p className="text-sm text-muted-foreground">Comprehensive activity and transaction log</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" iconName="Filter" iconPosition="left" size="sm">
              Advanced Filters
            </Button>
            <Button variant="outline" iconName="Download" iconPosition="left" size="sm">
              Export
            </Button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Input
              type="search"
              placeholder="Search activities, users, or transaction IDs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="w-full"
            />
          </div>
          <div className="flex gap-2">
            {filterOptions?.map(option => (
              <button
                key={option?.value}
                onClick={() => setSelectedFilter(option?.value)}
                className={`
                  px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200
                  ${selectedFilter === option?.value
                    ? 'bg-accent text-accent-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }
                `}
              >
                {option?.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">Timestamp</th>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">Action</th>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">User</th>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">Details</th>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">Status</th>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">Risk</th>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredRecords?.map((record, index) => {
              const { date, time } = formatTimestamp(record?.timestamp);
              
              return (
                <tr key={record?.id} className={`border-b border-border hover:bg-muted/30 transition-colors duration-200 ${index % 2 === 0 ? 'bg-background' : 'bg-muted/10'}`}>
                  <td className="p-4">
                    <div className="text-sm">
                      <div className="font-medium text-foreground">{date}</div>
                      <div className="text-muted-foreground">{time}</div>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <Icon name={getActionIcon(record?.action)} size={16} className="text-muted-foreground" />
                      <div>
                        <div className="text-sm font-medium text-foreground">{record?.action}</div>
                        <div className="text-xs text-muted-foreground">{record?.category}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="text-sm">
                      <div className="font-medium text-foreground">{record?.user}</div>
                      <div className="text-xs text-muted-foreground">{record?.ipAddress}</div>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="text-sm text-foreground max-w-xs">
                      <div className="truncate">{record?.details}</div>
                      <div className="text-xs text-muted-foreground font-mono">{record?.transactionId}</div>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(record?.status)}`}>
                      {record?.status?.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="p-4">
                    <span className={`text-sm font-medium ${getRiskScoreColor(record?.riskScore)}`}>
                      {record?.riskScore?.toUpperCase()}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" iconName="Eye" />
                      <Button variant="ghost" size="sm" iconName="MoreHorizontal" />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {filteredRecords?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No records found</h3>
          <p className="text-muted-foreground">Try adjusting your search terms or filters.</p>
        </div>
      )}
      <div className="p-4 border-t border-border bg-muted/20">
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>Showing {filteredRecords?.length} of {auditRecords?.length} records</span>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" iconName="ChevronLeft" disabled />
            <span className="px-2">Page 1 of 1</span>
            <Button variant="ghost" size="sm" iconName="ChevronRight" disabled />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditTrail;